import { DEFAULT_PARAMS } from './defaults';
import { mergeParams } from './funnel';
import type { FunnelParams, FunnelParamsPatch } from './types';

/**
 * What changed between two scenarios, and how to read a revision log.
 *
 * Pure, like the rest of `model/`. Two deliberate boundaries:
 *
 *  - **Changes are structured, never formatted.** A `ParamChange` carries raw
 *    numbers and a `kind`; the caller formats with `formatCount` /
 *    `formatPercent` from `@/lib/format`. Presentation stays out of the model,
 *    the same way `roundForDisplay` splits maths from rendering.
 *  - **`stage` is a local union, not `StageId` from `layout/`.** `layout`
 *    depends on `model`, so importing back the other way would invert the
 *    layering for a three-word type.
 */

/** Which drawer stage edits this field. `reach` is absent: it is fully derived. */
export type ChangeStage = 'source' | 'screen' | 'chase';

export type ParamChangeKind = 'count' | 'rate' | 'share' | 'name' | 'added' | 'removed';

export type ParamChange = {
  /** Stable identity for the changed thing, e.g. `subpopulations.sub-3f2a.reach`. */
  path: string;
  /** Human label, e.g. `Centerwell — reached`. */
  label: string;
  stage: ChangeStage;
  kind: ParamChangeKind;
  from: number | string | null;
  to: number | string | null;
};

const SUBPOPULATION_FIELDS = {
  count: { label: 'people', kind: 'count' },
  reach: { label: 'reached', kind: 'rate' },
  screenRate: { label: 'screen rate', kind: 'rate' },
} as const;

const EXCLUSION_FIELDS = {
  count: { label: 'people', kind: 'count' },
} as const;

const COHORT_FIELDS = {
  share: { label: 'share of chase pool', kind: 'share' },
  reach: { label: 'reached', kind: 'rate' },
  completion: { label: 'completed', kind: 'rate' },
} as const;

const OUTCOME_FIELDS = {
  noFollowup: { label: 'PHQ-9 under 10 — measure closed', kind: 'rate' },
  followupRequired: { label: 'PHQ-9 10 or higher — owes follow-up', kind: 'rate' },
  phq2NegativeShare: { label: 'PHQ-2 under 3, no PHQ-9 needed', kind: 'rate' },
  cssrsShare: { label: 'also need a C-SSRS', kind: 'rate' },
} as const;

type FieldSpec = { label: string; kind: ParamChangeKind };

/**
 * Compares one keyed list, matching rows by id so a rename reads as a rename and
 * a reorder reads as nothing at all.
 *
 * Generic over the row type rather than taking a `Record<string, unknown>`:
 * interfaces have no implicit index signature, and widening them to one here
 * would also throw away the compile-time check that every key in `fields` is a
 * real field of the row.
 */
function diffList<T extends { id: string; name: string }>(
  listKey: string,
  stage: ChangeStage,
  before: readonly T[],
  after: readonly T[],
  fields: Partial<Record<Extract<keyof T, string>, FieldSpec>>,
): ParamChange[] {
  const changes: ParamChange[] = [];
  const beforeById = new Map(before.map((row) => [row.id, row]));
  const afterById = new Map(after.map((row) => [row.id, row]));

  for (const row of before) {
    if (afterById.has(row.id)) continue;
    changes.push({
      path: `${listKey}.${row.id}`,
      label: row.name,
      stage,
      kind: 'removed',
      from: row.name,
      to: null,
    });
  }

  for (const row of after) {
    const previous = beforeById.get(row.id);
    if (!previous) {
      changes.push({
        path: `${listKey}.${row.id}`,
        label: row.name,
        stage,
        kind: 'added',
        from: null,
        to: row.name,
      });
      continue;
    }

    if (previous.name !== row.name) {
      changes.push({
        path: `${listKey}.${row.id}.name`,
        label: 'renamed',
        stage,
        kind: 'name',
        from: previous.name,
        to: row.name,
      });
    }

    for (const [field, spec] of Object.entries(fields) as [
      Extract<keyof T, string>,
      FieldSpec,
    ][]) {
      const from = previous[field];
      const to = row[field];
      if (typeof from !== 'number' || typeof to !== 'number' || from === to) continue;
      changes.push({
        path: `${listKey}.${row.id}.${field}`,
        // The row names itself, so the label reads without extra context.
        label: `${row.name} — ${spec.label}`,
        stage,
        kind: spec.kind,
        from,
        to,
      });
    }
  }

  return changes;
}

/** Every field that differs between two complete scenarios. */
export function diffParams(before: FunnelParams, after: FunnelParams): ParamChange[] {
  const changes: ParamChange[] = [
    ...diffList(
      'subpopulations',
      'source',
      before.subpopulations,
      after.subpopulations,
      SUBPOPULATION_FIELDS,
    ),
    ...diffList(
      'exclusionPopulations',
      'source',
      before.exclusionPopulations,
      after.exclusionPopulations,
      EXCLUSION_FIELDS,
    ),
    ...diffList('followupCohorts', 'chase', before.followupCohorts, after.followupCohorts, COHORT_FIELDS),
  ];

  for (const [field, spec] of Object.entries(OUTCOME_FIELDS)) {
    const key = field as keyof typeof OUTCOME_FIELDS;
    const from = before.outcomes[key];
    const to = after.outcomes[key];
    if (from === to) continue;
    changes.push({
      path: `outcomes.${field}`,
      label: spec.label,
      stage: 'screen',
      kind: spec.kind,
      from,
      to,
    });
  }

  return changes;
}

/* ------------------------------------------------------------------------- *
 * Reading the revision log
 * ------------------------------------------------------------------------- */

export type RevisionOp = 'create' | 'update' | 'restore' | 'reset';

/** One row of the revision log, as the API sends it. */
export type RevisionMeta = {
  version: number;
  params: FunnelParamsPatch;
  op: RevisionOp;
  restoredFrom: number | null;
  sessionId: string | null;
  /** ISO 8601. */
  createdAt: string;
};

export type RevisionGroup = {
  /** Stable React key. */
  id: string;
  op: RevisionOp;
  restoredFrom: number | null;
  sessionId: string | null;
  /** Newest version in the group — the one a restore would target. */
  version: number;
  /** Oldest version in the group. Equal to `version` for a single revision. */
  earliestVersion: number;
  /** How many raw revisions collapsed into this entry. */
  revisionCount: number;
  /** ISO timestamp of the newest revision in the group. */
  at: string;
  /** Net change across the whole group. Empty for the initial `create`. */
  changes: ParamChange[];
};

/** Revisions closer together than this, from one session, read as one edit. */
export const COALESCE_WINDOW_MS = 2 * 60 * 1000;

/**
 * Collapses autosave noise into readable entries.
 *
 * The write path debounces at 750ms, so a rep typing a number produces a
 * revision per pause. The log keeps every one of them — it is append-only and
 * that is the point — so the tidying happens here, at read time, where it costs
 * nothing and destroys nothing.
 *
 * Only consecutive `update` revisions coalesce. `create`, `restore` and `reset`
 * are each an intentional, nameable act, so they always stand alone — which also
 * gives the edits that follow a creation something to be diffed against.
 *
 * Beyond that, a group breaks on a different session or a gap wider than
 * COALESCE_WINDOW_MS. A null session never groups, because two anonymous writes
 * cannot be shown to be the same person.
 *
 * Expects newest-first, the order the API returns, and preserves it.
 */
export function coalesceRevisions(revisions: readonly RevisionMeta[]): RevisionGroup[] {
  const full = revisions.map((revision) => ({
    revision,
    params: mergeParams(DEFAULT_PARAMS, revision.params),
  }));

  const groups: RevisionGroup[] = [];
  let index = 0;

  while (index < full.length) {
    const head = full[index];
    if (!head) break;

    let end = index; // Inclusive index of the oldest member of this group.
    if (head.revision.op === 'update') {
      while (end + 1 < full.length) {
        const next = full[end + 1];
        const current = full[end];
        if (!next || !current) break;
        if (next.revision.op !== 'update') break;
        if (!next.revision.sessionId || next.revision.sessionId !== head.revision.sessionId) break;
        const gap =
          Date.parse(current.revision.createdAt) - Date.parse(next.revision.createdAt);
        if (!Number.isFinite(gap) || gap > COALESCE_WINDOW_MS) break;
        end += 1;
      }
    }

    const oldest = full[end];
    // The state this group started from is the revision just older than it. When
    // there is none, this is the beginning of the scenario's history.
    const baseline = full[end + 1];
    if (!oldest) break;

    groups.push({
      id: `v${head.revision.version}`,
      op: head.revision.op,
      restoredFrom: head.revision.restoredFrom,
      sessionId: head.revision.sessionId,
      version: head.revision.version,
      earliestVersion: oldest.revision.version,
      revisionCount: end - index + 1,
      at: head.revision.createdAt,
      changes: baseline ? diffParams(baseline.params, head.params) : [],
    });

    index = end + 1;
  }

  return groups;
}
